import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DomainNode, DomainData, BadgeColors, BadgeType } from '../types';
import './CommandPalette.css';

interface CommandPaletteProps {
  data: DomainData | null;
}

interface FlatNode {
  name: string;
  description?: string;
  url?: string;
  icon?: string;
  badge?: BadgeType;
  priority?: string;
  path: string;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('cmd-palette-recent') || '[]');
    } catch {
      return [];
    }
  });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Flatten tree data into searchable list
  const flattenedNodes = useMemo((): FlatNode[] => {
    if (!data) return [];
    
    const nodes: FlatNode[] = [];
    
    const flatten = (node: DomainNode, path: string = '') => {
      const currentPath = path ? `${path} > ${node.name}` : node.name;
      
      if (node.url) {
        nodes.push({
          name: node.name,
          description: node.description,
          url: node.url,
          icon: node.icon,
          badge: node.badge,
          priority: node.priority,
          path: currentPath
        });
      }
      
      node.children?.forEach(child => flatten(child, currentPath));
    };
    
    data.children.forEach(child => flatten(child));
    return nodes;
  }, [data]);

  // Filter nodes based on query
  const filteredNodes = useMemo(() => {
    if (!query.trim()) {
      // Show recent items first, then all items
      const recent = flattenedNodes.filter(n => recentItems.includes(n.url || ''));
      const others = flattenedNodes.filter(n => !recentItems.includes(n.url || ''));
      return [...recent, ...others].slice(0, 10);
    }
    
    const lowerQuery = query.toLowerCase();
    return flattenedNodes
      .filter(node => 
        node.name.toLowerCase().includes(lowerQuery) ||
        node.description?.toLowerCase().includes(lowerQuery) ||
        node.url?.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 10);
  }, [flattenedNodes, query, recentItems]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setQuery('');
        setSelectedIndex(0);
      }
      
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle navigation within list
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredNodes.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredNodes[selectedIndex]) {
      e.preventDefault();
      openNode(filteredNodes[selectedIndex]);
    }
  }, [filteredNodes, selectedIndex]);

  // Open selected node
  const openNode = useCallback((node: FlatNode) => {
    if (node.url) {
      // Add to recent
      const newRecent = [node.url, ...recentItems.filter(r => r !== node.url)].slice(0, 5);
      setRecentItems(newRecent);
      localStorage.setItem('cmd-palette-recent', JSON.stringify(newRecent));
      
      // Open URL
      window.open(node.url, '_blank', 'noopener,noreferrer');
      setIsOpen(false);
    }
  }, [recentItems]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector('.cmd-item.selected');
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const renderBadge = (badge?: BadgeType) => {
    if (!badge) return null;
    return (
      <span 
        className={`cmd-badge cmd-badge-${badge.toLowerCase()}`}
        style={{ backgroundColor: BadgeColors[badge] }}
      >
        {badge}
      </span>
    );
  };

  // No visible trigger - use ⌘K keyboard shortcut only
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="cmd-overlay" onClick={() => setIsOpen(false)} />
      <div className="cmd-palette">
        <div className="cmd-header">
          <span className="cmd-search-icon">🔍</span>
          <input
            ref={inputRef}
            type="text"
            className="cmd-input"
            placeholder="Search domains, apps, or type a URL..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
          />
          <kbd className="cmd-kbd">ESC</kbd>
        </div>
        
        <div className="cmd-list" ref={listRef}>
          {filteredNodes.length === 0 ? (
            <div className="cmd-empty">
              No results found for "{query}"
            </div>
          ) : (
            <>
              {!query && recentItems.length > 0 && (
                <div className="cmd-section-label">Recent</div>
              )}
              {filteredNodes.map((node, idx) => (
                <div
                  key={node.url}
                  className={`cmd-item ${idx === selectedIndex ? 'selected' : ''}`}
                  onClick={() => openNode(node)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="cmd-item-icon">{node.icon || '🔗'}</span>
                  <div className="cmd-item-content">
                    <div className="cmd-item-title">
                      {node.name}
                      {renderBadge(node.badge)}
                    </div>
                    {node.description && (
                      <div className="cmd-item-desc">{node.description}</div>
                    )}
                    <div className="cmd-item-url">{node.url?.replace('https://', '')}</div>
                  </div>
                  <span className="cmd-item-action">↵</span>
                </div>
              ))}
            </>
          )}
        </div>
        
        <div className="cmd-footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>ESC</kbd> Close</span>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;

